import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Attribute } from 'src/app/models/attribute';
import { Customer } from 'src/app/models/customer';
import { DocumentType } from 'src/app/models/document-type';
import { Rights } from 'src/app/models/enums/rights';
import { FolderState } from 'src/app/models/folder-state';
import { IProcessDefinition } from 'src/app/models/interfaces/process-definition.interface';
import { ProcessDefinition } from 'src/app/models/process-definition';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { CustomersService } from 'src/app/services/customers.service';
import { DocumentTypesService } from 'src/app/services/document-type.service';
import { FoldersStatesService } from 'src/app/services/folders-states.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ProcessDefinitionService } from 'src/app/services/process-definition.service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { ProcessDefinitionDetailContext } from './process-definition-detail-context';
import { ProcessDefinitionFormContext } from './process-definition-form-context';
import { ProcessDefinitionsDetailComponent } from './process-definitions-detail/process-definitions-detail.component';
import { ProcessDefinitionsListComponent } from './process-definitions-list/process-definitions-list.component';

enum DisplayMode {
    List,
    Form,
}
@Component({
    selector: 'app-process-definitions',
    templateUrl: './process-definitions.component.html',
    styleUrls: ['./process-definitions.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ProcessDefinitionsListComponent, DetailHeaderComponent, ProcessDefinitionsDetailComponent],
})
export class ProcessDefinitionsComponent extends DefaultManagementComponent<IProcessDefinition, ProcessDefinition, number> implements OnInit {
    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre processus.
     */
    public defaultContext: ProcessDefinitionDetailContext = new ProcessDefinitionDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Liste des clients de l'organisation courante.
     */
    customers: Customer[];

    /**
     * Liste des types de documents de l'organisation courante.
     */
    documentTypes: DocumentType[];

    /**
     * Liste des attributs de l'organisation courante.
     */
    attributes: Attribute[];

    /**
     * Liste des états de dossier de l'organisation courante.
     */
    folderStates: FolderState[];

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param ChannelService : Service métier utilisé pour gérer les clients.
     */
    constructor(
        protected override notificationService: NotificationService,
        private processDefinitionService: ProcessDefinitionService,
        private customersService: CustomersService,
        private documentTypesService: DocumentTypesService,
        private folderStatesService: FoldersStatesService,
        private attributesService: AttributeDefinitionsService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ProcessCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ProcessEdit);

        this.loadCustomers();
        this.loadDocumentTypes();
        this.loadAttributes();
        this.loadFolderStates();
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param processDefinition : Valeur de la définition de processus retournée par le formulaire.
     */
    public onItemUpdated(processDefinition: ProcessDefinition): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.processDefinitionService.update(this.context.editedItemId, processDefinition).subscribe(
                (x) => {
                    this.itemsList.refresh();
                    this.displayForm(ProcessDefinition.createFormCopy(x), FormMode.Reading);
                    this.notificationService.displaySuccess(null, 'Définition de processus mis à jour');
                    this.setDisplayMode(DisplayMode.Form);
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour la definition de processus.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.processDefinitionService.add(processDefinition).subscribe(
                (x) => {
                    this.itemsList.refresh();
                    this.displayForm(ProcessDefinition.createFormCopy(x), FormMode.Reading);
                    this.notificationService.displaySuccess(null, 'Définition de processus créée');
                    this.setDisplayMode(DisplayMode.Form);
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer la définition de processus.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un processus.
     * @param processDefinition Définition de processus selectionné.
     */
    public onItemSelected(processDefinition: ProcessDefinition) {
        this.spinner.show('main-spinner');
        this.loadProcessDefinition(processDefinition.id).subscribe(
            (x) => {
                this.displayForm(ProcessDefinition.createFormCopy(x), FormMode.Reading);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher la définition de processus." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param processDefinition Définition de processus sélectionné.
     */
    public onItemDisplayed(processDefinition: ProcessDefinition) {
        this.spinner.show('main-spinner');
        this.loadProcessDefinition(processDefinition.id).subscribe(
            (x) => {
                this.displayForm(ProcessDefinition.createFormCopy(x), FormMode.Reading);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher la définition de processus." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param processDefinition Définition de processus sélectionné.
     */
    public onItemEdited(processDefinition: ProcessDefinition) {
        this.spinner.show('main-spinner');
        // Cas de la mise à jour.
        this.loadProcessDefinition(processDefinition.id).subscribe(
            (x) => {
                this.displayForm(ProcessDefinition.createFormCopy(x), FormMode.Edition);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer la définition de processus." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param process Processus sélectionné.
     */
    public onItemDuplicated(process: ProcessDefinition) {
        let duplicatedProcess = ProcessDefinition.createFormCopy(process);
        duplicatedProcess.id = null;
        //duplicatedProcess.definitionJson = null;
        this.displayForm(duplicatedProcess, FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onItemCreated() {
        this.displayForm(ProcessDefinition.createFormCopy(new ProcessDefinition()), FormMode.Creation);
    }

    /**
     * Appellé lors de l'export
     */
    public onItemsExported() {
        this.createContext();
        this.itemsList.export('Définition de processus.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onItemsImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onHelpRequested() {
        this.itemsList.displayHelpDialog('processDefinitions');
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param processDefinition Définition de processus sélectionnée.
     */
    public onItemDeleted(processDefinition: ProcessDefinition) {
        this.spinner.show('main-spinner');
        // Suppression effective du processus.
        this.processDefinitionService.delete(processDefinition.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Processus supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer le processus.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge les données d'une définition de processus.
     * @param processDefinitionId : Id de la definition de processus à charger.
     */
    public loadProcessDefinition(processDefinitionId: number): Observable<ProcessDefinition> {
        return this.processDefinitionService.get(processDefinitionId);
    }

    /**
     * Charge la liste des clients proposés dans les listes déroulante de clients du formulaire.
     */
    private loadCustomers() {
        this.spinner.show('main-spinner');

        const sortInfos = new SortInfos('Name', 'asc');
        this.customersService.List(null, null, null, sortInfos, 0, 1000).subscribe(
            (x) => {
                this.customers = x.value;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des clients.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge la liste des clients proposés dans les listes déroulante de clients du formulaire.
     */
    private loadDocumentTypes() {
        this.spinner.show('main-spinner');
        this.documentTypesService.List(null, null, null, null, 0, 1000).subscribe(
            (x) => {
                this.documentTypes = x.value;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des types de documents.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge la liste des attributs de l'organisation.
     */
    private loadAttributes() {
        this.spinner.show('main-spinner');
        this.attributesService.List(null, null, null, null, 0, 1000).subscribe(
            (x) => {
                this.attributes = x.value;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des attributs.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge la liste des états de dossier de l'organisation.
     */
    private loadFolderStates() {
        this.spinner.show('main-spinner');
        this.folderStatesService.List(null, null, null, null, 0, 1000).subscribe(
            (x) => {
                this.folderStates = x.value;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de charger la liste des états de dossier.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param processDefinition : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(processDefinition: IProcessDefinition): ProcessDefinitionDetailContext {
        let context = this.createContext();
        context.formContext.item = processDefinition;
        context.editedItemId = processDefinition.id;
        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): ProcessDefinitionDetailContext {
        let context: ProcessDefinitionDetailContext = new ProcessDefinitionDetailContext();
        (context.formContext as ProcessDefinitionFormContext).customers = this.customers;
        (context.formContext as ProcessDefinitionFormContext).documentTypes = this.documentTypes;
        (context.formContext as ProcessDefinitionFormContext).attributes = this.attributes;
        (context.formContext as ProcessDefinitionFormContext).folderStates = this.folderStates;
        return context;
    }
}
